import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import styled from "styled-components/macro";

import flocknoteStateHooks from "Flocknote/stateHooks";

import API from "Signups/API";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useNewSignupCreationFlow } from "Signups/SignupView/NewSignup";
import { checkForPaidRespondents } from "Signups/SignupView/SignupSheet/utils/checkForPaidRespondents";
import { useGetSignupListType } from "Signups/SignupView/utils/useGetSignupListType";
import { useSignups } from "Signups/state";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import FnrErrorText from "UI/FnrErrorText";
import FnrInput2 from "UI/FnrInput2";
import { colors } from "UI/colors";

import { getAmountInDollarsClean } from "Utilities/Amount/getAmountInDollarsClean";

import {
  useIsLoading,
  useIsNew,
  useIsOnlyItem,
  useSignupItem,
} from "../../EditSignupItemNav/statehooks";

import DeleteItemButton from "./DeleteItemButton";
import InfoOption from "./InfoOption";
import ItemInfoNumberInput from "./ItemInfoNumberInput";

Info.propTypes = {
  title: PropTypes.string,
  maxSingleUserSlots: PropTypes.number,
  totalSlots: PropTypes.number,
  ID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  respondents: PropTypes.array,
  showRespondentsCount: PropTypes.bool,
  isNew: PropTypes.bool,
  isOnlyItem: PropTypes.bool,
  isLoading: PropTypes.bool,
};

const DEFAULT_INFO_VALUES = {
  title: "New Item",
  maxSingleUserSlots: 5,
  totalSlots: 20,
  showRespondentsCount: true,
};

export default function Info() {
  const isNew = useIsNew();
  const isOnlyItem = useIsOnlyItem();
  const isLoading = useIsLoading();
  const [signupItem, setSignupItem] = useSignupItem();

  const {
    ID,
    title = DEFAULT_INFO_VALUES.title,
    maxSingleUserSlots,
    totalSlots,
    respondents,
    showRespondentsCount,
  } = signupItem;

  const history = useHistory();
  const [
    {
      currentSignupList: {
        ID: signupListID,
        isPerSignupCostType,
        fundIt: { fund },
      },
    },
    dispatch,
  ] = useSignups();

  const { isPollType } = useGetSignupListType();
  const { isArchived } = useIsSignupClosed();
  const [{ hasSignupsProfileFields }] =
    flocknoteStateHooks.useNetworkMetadata();

  const {
    forceToAddItem,
    isInCreationFlow,
    setFirstItemCreated,
    firstItemCreated,
    beginItemCreationFlow,
    setLastCreatedSignupItemID,
  } = useNewSignupCreationFlow();

  /**
     Remember, one "blank default" item is created with every signup!
     although this item, as far as the backend is concerned, is like any other,
     we treat this "blank default" item as if it were "new" (i.e. not yet persisted) to offer a better UX
     For the time being,
     we determine when we are looking at this item by checking against the (brittle) DEFAULT_INFO_VALUES,
     which are NOT currently pulled from the backend
     */
  let isBlankDefault = false;
  if (isOnlyItem) {
    const info = {
      title,
      maxSingleUserSlots,
      totalSlots,
      showRespondentsCount,
    };
    isBlankDefault = ["title", "maxSingleUserSlots", "totalSlots"].reduce(
      (isEqual, infoProp) =>
        isEqual && info[infoProp] === DEFAULT_INFO_VALUES[infoProp],
      true
    );
  }

  const shouldBehaveLikeNew = isNew || isBlankDefault || forceToAddItem;
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveBtn, setShowSaveBtn] = useState(false);

  const [stagedValues, setStagedValues] = useState({
    title:
      shouldBehaveLikeNew && title === DEFAULT_INFO_VALUES.title ? "" : title,
    maxSingleUserSlots,
    totalSlots,
    showRespondentsCount,
  });

  useEffect(() => {
    setStagedValues({
      title:
        shouldBehaveLikeNew && signupItem.title === DEFAULT_INFO_VALUES.title
          ? ""
          : signupItem.title,
      maxSingleUserSlots:
        signupItem.maxSingleUserSlots || DEFAULT_INFO_VALUES.maxSingleUserSlots,
      totalSlots: signupItem.totalSlots || DEFAULT_INFO_VALUES.totalSlots,
      showRespondentsCount: shouldBehaveLikeNew
        ? DEFAULT_INFO_VALUES.showRespondentsCount
        : signupItem.showRespondentsCount,
    });
  }, [signupItem, shouldBehaveLikeNew]);

  useEffect(() => {
    if (!isNew) return;
    if (title !== DEFAULT_INFO_VALUES.title) return;

    setStagedValues({ ...DEFAULT_INFO_VALUES, title: "" });
  }, [isNew, title]);

  const [error, setError] = useState("");

  const hasValidProps = isNew || ID > 0;
  if (!hasValidProps) {
    return null;
  }

  const stagedMaxSingleUserSlotsAsNum = +stagedValues.maxSingleUserSlots;
  const stagedTotalSlotsAsNum = +stagedValues.totalSlots;

  const inputsDisabled = isSaving || isArchived || isLoading;

  const updateStagedValue = (key) => (value) => {
    setShowSaveBtn(true);
    setStagedValues({
      ...stagedValues,
      [key]: value,
    });
  };

  const create = () => {
    beginItemCreationFlow(signupListID);

    return API.createItem(signupListID, {
      signupListID,
      title: stagedValues.title,
      maxSlotsPerSingleSignup: stagedValues.maxSingleUserSlots,
      maxSlots: stagedValues.totalSlots,
      showRespondentsCount: stagedValues.showRespondentsCount,
    })
      .post()
      .then(({ signupListItem }) => {
        if (
          !(
            typeof signupListItem?.ID === "number" &&
            signupListItem?.ID > 0 &&
            typeof signupListItem?.orderNumber === "number"
          )
        ) {
          console.error(
            `Unable to retrieve signupListItemID (${signupListItem?.ID}) or orderNumber (${signupListItem?.orderNumber}) from server.`
          );
          setError(getErrorMsg(stagedValues.title));
          setIsSaving(false);
          return -1;
        }

        // Apply our transform when hydrating amounts (Cents as Number) from the backend
        signupListItem.amount = getAmountInDollarsClean(signupListItem.amount);

        // TODO scrollIntoView() the item
        dispatch({
          type: "Add Blank Signup Item",
          signupItem: { ...signupListItem, name: signupListItem.title },
        });
        dispatch({
          type: "Update All Signups Item Available Slots",
          payload: {
            ID: signupListID,
            additionalAvailableSlots: stagedTotalSlotsAsNum,
          },
        });

        setIsSaving(false);
        setError("");

        setFirstItemCreated(true);
        setLastCreatedSignupItemID(signupListItem.ID);

        return signupListItem.ID;
      })
      .catch((e) => {
        setIsSaving(false);
        console.error(e.message);
        setError(getErrorMsg(stagedValues.title));
        return -1;
      });
  };

  const edit = () =>
    API.editItem(signupListID, {
      signupListID,
      signupListItemID: ID,
      title: stagedValues.title,
      maxSlotsPerSingleSignup: stagedValues.maxSingleUserSlots,
      maxSlots: stagedValues.totalSlots,
      showRespondentsCount: stagedValues.showRespondentsCount,
    })
      .post()
      .then(() => {
        // Update values for this item in the `state.currentSignupList.signupItems` array
        dispatch({
          type: "SignupList Item Edited",
          ID,
          key: "name",
          value: stagedValues.title,
        });
        dispatch({
          type: "SignupList Item Edited",
          ID,
          key: "title",
          value: stagedValues.title,
        });
        dispatch({
          type: "SignupList Item Edited",
          ID,
          key: "maxSingleUserSlots",
          value: stagedMaxSingleUserSlotsAsNum,
        });
        dispatch({
          type: "SignupList Item Edited",
          ID,
          key: "totalSlots",
          value: stagedTotalSlotsAsNum,
        });

        dispatch({
          type: "SignupList Item Edited",
          ID,
          key: "showRespondentsCount",
          value: stagedValues.showRespondentsCount,
        });

        // Update values in state.currentSignupItem
        setSignupItem({
          ...signupItem,
          maxSingleUserSlots: stagedMaxSingleUserSlotsAsNum,
          totalSlots: stagedTotalSlotsAsNum,
          title: stagedValues.title,
          showRespondentsCount: stagedValues.showRespondentsCount,
        });

        // updates the total slots available in the all signups sidebar
        if (totalSlots !== stagedValues.totalSlots) {
          const difference = stagedTotalSlotsAsNum - totalSlots;
          dispatch({
            type: "Update All Signups Item Available Slots",
            payload: {
              ID: signupListID,
              additionalAvailableSlots: difference,
            },
          });
        }

        setFirstItemCreated(true);
        setError("");
        setShowSaveBtn(false);
        return ID;
      })
      .catch((e) => {
        console.error(e);
        setError(getErrorMsg(title, true));
        return -1;
      })
      .finally(() => {
        setIsSaving(false);
      });

  const submit = async (e) => {
    e.preventDefault();

    if (stagedValues.title.replace(/&nbsp;/g, "").trim().length === 0) {
      setError("Please give this item a title.");
      return;
    }

    if (stagedMaxSingleUserSlotsAsNum <= 0) {
      setError(
        "Please specify the maximum slots per item as a number greater than zero."
      );
      return;
    }

    if (stagedTotalSlotsAsNum <= 0) {
      setError(
        "Please specify the total available slots as a number greater than zero."
      );
      return;
    }

    if (stagedMaxSingleUserSlotsAsNum > stagedTotalSlotsAsNum) {
      setError("Total Available Slots cannot be less than Max Slots Per Item");
      return;
    }

    setIsSaving(true);
    const newID = ID === "new";
    const save = isNew && newID ? create : edit;
    const itemID = await save();

    if (!(typeof itemID === "number" && itemID > 0)) {
      return;
    }

    if (!shouldBehaveLikeNew) return;

    if (hasSignupsProfileFields)
      return history.replace(
        `/signups/${signupListID}/items/${itemID}/additional-info`
      );

    if (fund?.ID > 0 && !isPerSignupCostType) {
      history.push(`/signups/${signupListID}/items/${itemID}/fund-it`);
      return;
    }

    if (isInCreationFlow)
      return history.push(`/signups/${signupListID}/items/another`);

    if (isNew) {
      return history.replace(`/signups/${signupListID}/items/${itemID}/info`);
    }
    // isBlankDefault => we don't need to go anywhere
  };

  return (
    <Form onSubmit={submit} key={ID}>
      <TitleSection>
        <FnrInput2
          data-cy="Info_TitleInput"
          value={stagedValues.title || ""}
          setValue={updateStagedValue("title")}
          label="Title"
          disabled={inputsDisabled}
          autofocus={shouldBehaveLikeNew ? 300 : undefined}
        />
      </TitleSection>
      
      {!isPollType && (
        <>
          <Section>
            <ItemInfoNumberInput
              data-cy="Info_MaxSlots"
              id="signup-list-item-info-max-slots-per-item"
              label="Max Slots Per Item:"
              value={stagedValues.maxSingleUserSlots || ""}
              setValue={updateStagedValue("maxSingleUserSlots")}
              disabled={inputsDisabled}
            >
              The maximum number of slots that an individual can sign up for.
              For instance, with a maximum limit of 5, a person can register
              themselves, their spouse, and up to three children for this
              specific item.
            </ItemInfoNumberInput>
          </Section>
          <Divider />
          <Section>
            <ItemInfoNumberInput
              data-cy="Info_TotalSlots"
              id="signup-list-item-info-total-slots"
              label="Total Available Slots:"
              value={stagedValues.totalSlots || ""}
              setValue={updateStagedValue("totalSlots")}
              disabled={inputsDisabled}
            >
              The total number of slots allocated for this item. This does not
              account for those already signed up, and is NOT the number of
              remaining slots.
            </ItemInfoNumberInput>
          </Section>
          <Divider />
        </>
      )}

      <Section>
        <InfoOption
          data-cy="Info_ShowRespondentsCount"
          label="Show how many people have signed up for this item"
          isChecked={stagedValues.showRespondentsCount}
          onClick={updateStagedValue("showRespondentsCount")}
          disabled={inputsDisabled}
        />
      </Section>

      {error.length > 0 && <ErrorText error>{error}</ErrorText>}

      <ButtonWrapper>
        {showSaveBtn || shouldBehaveLikeNew ? (
          <FnrButton
            data-cy="SignupInfo_SaveButton"
            type="submit"
            color={
              !shouldBehaveLikeNew ||
              (firstItemCreated && typeof ID === "number")
                ? "green"
                : "blue"
            }
            loading={isSaving}
            disabled={isSaving}
          >
            <ButtonText>
              {!shouldBehaveLikeNew ||
              (firstItemCreated && typeof ID === "number")
                ? "Save"
                : fund?.ID > 0 && !isPerSignupCostType
                ? "Next"
                : "Add Item"}
            </ButtonText>
            {shouldBehaveLikeNew && fund?.ID > 0 && !isPerSignupCostType && (
              <ButtonIcon fas fa-chevron-right />
            )}
          </FnrButton>
        ) : (
          <div />
        )}

        {!(isNew || isOnlyItem || isArchived) && (
          <DeleteItemButton
            dispatch={dispatch}
            signupListID={signupListID}
            itemID={ID}
            numRespondents={respondents?.length}
            hasAnyPaidRespondents={checkForPaidRespondents(respondents)}
            isDisabled={isSaving}
          />
        )}
      </ButtonWrapper>
    </Form>
  );
}

function getErrorMsg(title, isEditing = false) {
  return `Sorry, there was a problem ${isEditing ? "editing" : "creating"} ${
    title || "this item"
  }.`;
}

const Form = styled.form`
  max-width: 460px; // normalizes maxWidth with the respondents tab content
  color: ${colors.fnBlue700};
  font-weight: 500;
`;

const Section = styled.div`
  padding: 1rem 0;
`;

const TitleSection = styled.div`
  padding: 1rem 0;
  margin-left: 1px;
`;

const ErrorText = styled(FnrErrorText)`
  margin: 1rem 0.5rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row-reverse;
  margin-top: 1.5rem;
`;

const Divider = styled.div`
  border-bottom: 1px solid ${colors.fnGray100};
`;
